import { getPlanStyle } from '../../utils/planStyles';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBackendURL } from '../../utils/backendURL';
import { 
  Users, 
  DollarSign, 
  TestTube, 
  TrendingUp, 
  AlertCircle,
  Calendar,
  Activity,
  Crown,
  Zap
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color, subtitle, trend }) => (
  <div className="admin-card p-6 hover:shadow-lg transition-shadow">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-full ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
    </div>
    {trend && (
      <div className="mt-4 flex items-center">
        <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
        <span className="text-sm text-green-600 font-medium">{trend}</span>
      </div>
    )}
  </div>
);

const DashboardOverview = ({ onNavigate }) => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const BACKEND_URL = getBackendURL();

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/ops/dashboard/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch overview:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="admin-card p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-card p-6 border-red-200 bg-red-50">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
        <button
          onClick={fetchOverview}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const userCounts = overview?.user_counts || {};
  const totalUsers = Object.values(userCounts).reduce((sum, count) => sum + count, 0);

  return (
    <div className="space-y-8">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={totalUsers.toLocaleString()}
          icon={Users}
          color="bg-gradient-to-r from-blue-500 to-blue-600"
          subtitle={`${userCounts.free || 0} free, ${(totalUsers - (userCounts.free || 0))} paid`}
          trend="+12% this month"
        />
        
        <StatCard
          title="Monthly Revenue"
          value={`$${(overview?.monthly_revenue || 0).toLocaleString()}`}
          icon={DollarSign}
          color="bg-gradient-to-r from-green-500 to-green-600"
          subtitle="MRR (Monthly Recurring Revenue)"
          trend="+8% vs last month"
        />
        
        <StatCard
          title="Tests Generated"
          value={(overview?.monthly_tests || 0).toLocaleString()}
          icon={TestTube}
          color="bg-gradient-to-r from-purple-500 to-purple-600"
          subtitle="This month"
          trend="+23% vs last month"
        />
        
        <StatCard
          title="Active Subscriptions"
          value={(totalUsers - (userCounts.free || 0)).toLocaleString()}
          icon={Crown}
          color="bg-gradient-to-r from-orange-500 to-orange-600"
          subtitle="Paying customers"
          trend="+15% this month"
        />
      </div>

      {/* Plan Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="admin-card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Distribution</h3>
          <div className="space-y-4">
            {Object.entries(userCounts).map(([plan, count]) => {
              const percentage = totalUsers ? ((count / totalUsers) * 100).toFixed(1) : 0;
              return (
                <div key={plan} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full ${getPlanStyle(plan).dot} mr-3`}></div>
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {plan.replace(/_/g, ' ')} Plan
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">{count} users</span>
                    <span className="text-xs text-gray-400">({percentage}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Most Active Users */}
        <div className="admin-card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Active Users</h3>
          <div className="space-y-3">
            {(overview?.most_active_users || []).slice(0, 5).map((user, index) => (
              <div key={user._id || user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white text-xs font-medium">
                      {(user.name || user.email)?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {user.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-600">{user.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPlanStyle(user.plan).badge}`}>
                    {user.plan?.replace(/_/g, ' ')}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {user.test_count || 0} tests
                  </p>
                </div>
              </div>
            ))}
            
            {(!overview?.most_active_users || overview.most_active_users.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No active users in the last 30 days</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="admin-card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button type="button" onClick={() => onNavigate('users')} className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
            <Users className="h-5 w-5 text-gray-400 mr-2" />
            <span className="text-sm text-gray-600">Manage Users</span>
          </button>
          
          <button type="button" onClick={() => onNavigate('analytics')} className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
            <Zap className="h-5 w-5 text-gray-400 mr-2" />
            <span className="text-sm text-gray-600">View Analytics</span>
          </button>
          
          <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
            <Calendar className="h-5 w-5 text-gray-400 mr-2" />
            <span className="text-sm text-gray-600">Export Reports</span>
          </button>
        </div>
      </div>

      {/* System Status */}
      <div className="admin-card p-6 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">System Status</h3>
            <p className="text-sm text-gray-600">All systems operational</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-green-700">Online</span>
          </div>
        </div>
        
        <div className="mt-4 text-xs text-gray-500">
          Last updated: {new Date(overview?.last_updated).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;